import { useSelector } from 'dva';

export const COMMERCIAL_BILLING_SECTION_KEY = 'commercial-and-billing';

export const COMMERCIAL_BILLING_NAV_KEYS = Object.freeze({
  ONBOARDING: 'onboarding',
  SUBSCRIPTIONS: 'subscriptions',
  DISTRIBUTORS: 'distributors',
  PARTNERS: 'partners',
  RESELLERS: 'resellers',
  REFERRAL_AGENTS: 'referral-agents',
  CUSTOMER_ASSIGNMENTS: 'customer-assignments',
  COMMERCIAL_AGREEMENTS: 'commercial-agreements',
  PLANS: 'plans',
});

const NAV_ITEM_KEY_TO_FRONTEND_ROUTE = Object.freeze({
  [COMMERCIAL_BILLING_NAV_KEYS.ONBOARDING]: '/configuration/channel-onboarding',
  [COMMERCIAL_BILLING_NAV_KEYS.SUBSCRIPTIONS]: '/configuration/billing-subscriptions',
  [COMMERCIAL_BILLING_NAV_KEYS.DISTRIBUTORS]: '/configuration/channel-distributors',
  [COMMERCIAL_BILLING_NAV_KEYS.PARTNERS]: '/configuration/channel-partners',
  [COMMERCIAL_BILLING_NAV_KEYS.RESELLERS]: '/configuration/channel-resellers',
  [COMMERCIAL_BILLING_NAV_KEYS.REFERRAL_AGENTS]: '/configuration/channel-referral-agents',
  [COMMERCIAL_BILLING_NAV_KEYS.CUSTOMER_ASSIGNMENTS]: '/configuration/channel-customer-assignments',
  [COMMERCIAL_BILLING_NAV_KEYS.COMMERCIAL_AGREEMENTS]: '/configuration/channel-agreements',
  [COMMERCIAL_BILLING_NAV_KEYS.PLANS]: '/configuration/channel-plans',
});

/** Frontend route used by the configuration sidebar menu. */
export const resolveFrontendMenuRoute = (item = {}) => {
  if (item.key && NAV_ITEM_KEY_TO_FRONTEND_ROUTE[item.key]) {
    return NAV_ITEM_KEY_TO_FRONTEND_ROUTE[item.key];
  }

  if (item.path?.startsWith('/configuration/')) {
    return item.path;
  }

  return null;
};

/** Backend API base path without leading slash (e.g. billing/company-subscriptions). */
export const normalizeNavigationApiPath = (path) => {
  if (!path || typeof path !== 'string') return null;
  return path.replace(/^\/+/, '');
};

export const getCommercialBillingApiRoute = (sessionNavigation = {}, navigationKey) =>
  sessionNavigation.commercialBillingApiRoutes?.[navigationKey] ?? null;

export const getCommercialBillingApiSubRoute = (apiRoutes = {}, navigationKey, suffix) => {
  const baseRoute = apiRoutes?.[navigationKey];
  if (!baseRoute || !suffix) return null;
  return `${baseRoute}/${suffix}`;
};

export function* selectCommercialBillingApiRoute(navigationKey, select) {
  return getCommercialBillingApiRoute(
    yield select((state) => state.sessionNavigation),
    navigationKey,
  );
}

export const buildCommercialBillingSectionData = (section) => {
  if (!section?.items?.length) {
    return { menuItems: [], apiRoutes: {} };
  }

  const menuItems = [];
  const apiRoutes = {};

  section.items.forEach((item) => {
    if (item.visible === false || !item.key) return;

    const apiPath = normalizeNavigationApiPath(item.path);
    const frontendRoute = resolveFrontendMenuRoute(item);
    if (!apiPath || !frontendRoute) return;

    apiRoutes[item.key] = apiPath;
    menuItems.push({
      key: frontendRoute,
      label: item.label,
      disabled: item.enabled === false,
      disabledReason: item.disabledReason,
    });
  });

  return { menuItems, apiRoutes };
};

export const findCommercialBillingSection = (sections = []) =>
  sections.find((section) => section.key === COMMERCIAL_BILLING_SECTION_KEY);

/** Label from GET /me/navigation for commercial billing routes (matches sidebar menu). */
export const resolveCommercialBillingMenuLabel = (pathname, menuItems = []) => {
  if (!pathname || !menuItems.length) return null;
  return menuItems.find((item) => item.key === pathname)?.label ?? null;
};

export const useCommercialBillingApiRoute = (navigationKey) =>
  useSelector((state) => getCommercialBillingApiRoute(state.sessionNavigation, navigationKey));
